import { useEffect, useState } from 'react';

import useAuthentication from './useAuthentication';

export interface CurrentUser {
    email?: string;
    firstName?: string;
    lastName?: string;
    /**
     * L'adresse a-t-elle ete verifiee ? TROIS etats et non deux : `true`, `false`, et
     * **absent** quand le jeton ne porte pas le claim, ce qui arrive sur un jeton
     * federe. Les confondre ferait dire « non verifiee » a un ecran qui n'en sait rien.
     */
    emailVerified?: boolean;
    /**
     * La personne est-elle entree par un fournisseur d'identite externe ?
     *
     * Mesure sur un jeton reel : une session SSO porte `email_verified: false`, la
     * federation ne marquant pas l'adresse cote Cognito. Sans ce drapeau, un ecran qui
     * lit le seul claim conclut « non verifiee » de quelqu'un dont l'entreprise repond
     * de l'adresse, ce qui est l'inverse de la verite.
     */
    federated?: boolean;
}

/** Payload utile de l'idToken Cognito (claims OIDC standard). */
interface IdTokenPayload {
    email?: string;
    given_name?: string;
    family_name?: string;
    /**
     * Cognito le rend en BOOLEEN, mais un fournisseur federe peut le poser en CHAINE :
     * c'est un claim OIDC, et la specification ne contraint que le sens. Lire `=== true`
     * ferait donc passer une adresse verifiee pour non verifiee selon d'ou vient la
     * personne, ce qu'aucun test de ce depot n'attraperait.
     */
    email_verified?: boolean | string;
    /** Pose par Cognito sur une session federee, et absent sinon. */
    identities?: unknown;
}

const decodeJwtPayload = (token: string): IdTokenPayload | null => {
    try {
        const payload = token.split('.')[1];
        const normalized = payload.replace(/-/g, '+').replace(/_/g, '/');
        return JSON.parse(window.atob(normalized));
    } catch {
        return null;
    }
};

/** Identite de l'utilisateur connecte, lue dans l'idToken Cognito. */
const useCurrentUser = (): CurrentUser => {
    const { getAuthorizationToken } = useAuthentication();
    const [user, setUser] = useState<CurrentUser>({});

    useEffect(() => {
        const run = async () => {
            const token = await getAuthorizationToken();
            if (!token) {
                return;
            }
            const payload = decodeJwtPayload(token);
            if (!payload) {
                return;
            }
            setUser({
                email: payload.email,
                firstName: payload.given_name,
                lastName: payload.family_name,
                emailVerified:
                    payload.email_verified === undefined
                        ? undefined
                        : payload.email_verified === true || payload.email_verified === 'true',
                federated: payload.identities !== undefined,
            });
        };
        void run();
    }, []);

    return user;
};

export default useCurrentUser;
